// Reusable "Imprimir Etiqueta" button for a part.
//
// Behaviour:
//   - Fetches eligible print targets (shared bench users with allow_desktop_app=1).
//   - If there are no other targets AND ≤1 active unit → direct POST (quick path).
//   - Otherwise opens picker modal with unit list + target (posto) selector.
//
// Uses toasts for feedback. Shows yellow warning toast when API reports a
// lazy fallback (target was disabled and print was redirected to self).

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import Utils from "../utils";
import {
  getPartActiveUnits,
  printPartLabel,
} from "../services/LabelServices";
import axiosInstance from "../services/AxiosInstance";
import "./part-print-button.css";

const listPrintTargets = () =>
  axiosInstance.post("/users/print-targets", {});

// `hidden` turns off the visible trigger button — used when the parent
// already has its own UI element and triggers via `ref.current.trigger()`.
const PartPrintButton = forwardRef(({
  partId,
  className = "tp-btn-print",
  label,
  iconOnly = false,
  hidden = false,
  onPrinted,
}, ref) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState([]);
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState(null); // null = use user default
  const [defaultTargetId, setDefaultTargetId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listPrintTargets()
      .then((r) => {
        if (cancelled) return;
        const list = r?.data?.data || [];
        setTargets(list);
        const self = list.find((u) => u.is_self);
        const fallback =
          self?.default_print_target_user_id ?? self?.id ?? null;
        setDefaultTargetId(fallback);
        setTargetId(fallback);
      })
      .catch(() => {
        // silent — falls back to server-side default resolution
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasBenchChoice = targets.filter((u) => !u.is_self).length > 0;

  const sendPrint = async (body) => {
    try {
      const payload = { ...body };
      if (targetId != null) payload.target_user_id = targetId;
      const { data } = await printPartLabel(payload);
      if (data?.warning) {
        toast.warn(data.warning);
      } else {
        toast.success(data?.message || t("print_label_enqueued"));
      }
      onPrinted?.(data?.data);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
  };

  const handleClick = async () => {
    if (!partId || busy) return;
    setBusy(true);
    try {
      const { data } = await getPartActiveUnits({ part_id: partId });
      const list = data?.data?.units || [];
      if (list.length <= 1 && !hasBenchChoice) {
        await sendPrint({ part_id: partId });
        return;
      }
      setUnits(list);
      setSelected([]);
      setTargetId(defaultTargetId);
      setModalOpen(true);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleUnit = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const selectAll = () => setSelected(units.map((u) => u.qty_label_id));

  const closeModal = () => {
    setModalOpen(false);
    setUnits([]);
    setSelected([]);
  };

  const handlePrintAll = async () => {
    closeModal();
    await sendPrint({ part_id: partId });
  };

  const handlePrintSelected = async () => {
    if (!selected.length) return;
    closeModal();
    await sendPrint({ part_id: partId, qty_label_ids: selected });
  };

  const targetSelectValue = targetId == null ? "default" : String(targetId);

  const renderTargetPicker = () => {
    if (!hasBenchChoice) return null;
    return (
      <div className="tp-unit-target">
        <label className="tp-unit-target-label">
          <i className="bi bi-printer" /> Posto de impressão
        </label>
        <select
          className="tp-unit-target-select"
          value={targetSelectValue}
          onChange={(e) => {
            const raw = e.target.value;
            setTargetId(raw === "default" ? defaultTargetId : Number(raw));
          }}
        >
          {targets.map((u) => (
            <option key={u.id} value={u.id}>
              {u.is_self ? "🧍 " : u.online ? "🟢 " : "⚪ "}
              {u.name}
              {u.is_self ? " (tu)" : u.online ? "" : " · offline"}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // Expose `trigger()` so parents can fire the whole click flow without
  // rendering our button (used from PartsInventory row dropdown).
  useImperativeHandle(ref, () => ({ trigger: handleClick }), [handleClick]);

  return (
    <>
      {!hidden && (
        <button
          type="button"
          className={className}
          onClick={handleClick}
          disabled={busy || !partId}
          title={t("print_label")}
        >
          <i className="bi bi-printer" />
          {!iconOnly && <span>{label || t("print_label")}</span>}
        </button>
      )}

      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
        size="lg"
        className="tp-unit-picker-modal"
      >
        <ModalHeader toggle={closeModal}>
          <div>
            <div className="tp-unit-picker-title">
              {units.length > 1
                ? "Seleccionar unidade a imprimir"
                : "Confirmar impressão"}
            </div>
            <small className="tp-unit-picker-sub">
              {units.length} unidade{units.length === 1 ? "" : "s"} activa
              {units.length === 1 ? "" : "s"}
            </small>
          </div>
        </ModalHeader>
        <ModalBody>
          {renderTargetPicker()}
          {units.length > 1 && (
            <div className="tp-unit-list">
              {units.map((u) => {
                const isSel = selected.includes(u.qty_label_id);
                return (
                  <label
                    key={u.qty_label_id}
                    className={`tp-unit-row${isSel ? " is-selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleUnit(u.qty_label_id)}
                    />
                    <div className="tp-unit-meta">
                      <div className="tp-unit-main">
                        <span className="tp-unit-number">
                          Unidade {u.unit}
                        </span>
                      </div>
                      <div className="tp-unit-location">
                        <i className="bi bi-geo-alt" />
                        {u.location_path || "Sem localização"}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          {units.length === 1 && (
            <div className="tp-unit-single-summary">
              <i className="bi bi-tag" />
              <div>
                <div className="tp-unit-number">Unidade {units[0].unit}</div>
                <div className="tp-unit-location">
                  <i className="bi bi-geo-alt" />
                  {units[0].location_path || "Sem localização"}
                </div>
              </div>
            </div>
          )}
          {units.length > 1 && (
            <div className="tp-unit-select-toolbar">
              <button
                type="button"
                className="tp-btn-link"
                onClick={selectAll}
                disabled={selected.length === units.length}
              >
                Seleccionar todas
              </button>
              <button
                type="button"
                className="tp-btn-link"
                onClick={() => setSelected([])}
                disabled={!selected.length}
              >
                Limpar
              </button>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={closeModal}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={handlePrintAll}
          >
            <i className="bi bi-layers" />{" "}
            {units.length > 1 ? "Imprimir todas" : "Imprimir"}
          </button>
          {units.length > 1 && (
            <button
              type="button"
              className="tp-btn-primary"
              onClick={handlePrintSelected}
              disabled={!selected.length}
            >
              <i className="bi bi-printer" /> Imprimir seleccionadas
              {selected.length > 0 && (
                <span className="tp-btn-count">({selected.length})</span>
              )}
            </button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
});
PartPrintButton.displayName = "PartPrintButton";

export default PartPrintButton;
